import React, {
  forwardRef,
  MutableRefObject,
  RefObject,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { FormTemplate, FormTemplateSection } from 'data/template/template.types';
import MultiTab from 'shared/multi-tab';
import { TabProp } from 'shared/multi-tab/MultiTab';
import Form, {
  FieldUpdateHandlerParam,
} from 'shared/form';
import { EricssonNETargetInfoFormData, EricssonNETargetInfoMonitoringFormData } from 'data/target/target.types';

type Props = {
  id: string
  formTemplateSection: FormTemplateSection
  template: FormTemplate
  data: Partial<EricssonNETargetInfoFormData>
  isEditing: boolean
  i18nKeyPrefix: string
  onFieldUpdate?: (param: FieldUpdateHandlerParam, tabId?: string) => void
  onTabAdd?: () => void
  onTabRemove?: (id: string | number) => void
}

export type EricssonNETargetInfoTabsRef = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
}

export const EricssonNETargetInfoTabs = forwardRef<EricssonNETargetInfoTabsRef, Props>(({
  id,
  formTemplateSection,
  template,
  data,
  isEditing,
  i18nKeyPrefix,
  onFieldUpdate = () => null,
  onTabAdd = () => null,
  onTabRemove = () => null,
}: Props, ref) => {
  const tabRefs = useRef<(RefObject<Form>)[]>([]);
  const [invalidTabs, setInvalidTabs] = useState<{ [id: string]: boolean }>({});

  const formSection = 'EIMSMCNB';

  const getValidTabs = (
    tabFormRefs: MutableRefObject<RefObject<Form>[]>,
    forceValidation = false,
    forceTouched = true,
  ) => {
    const validFormTabs = tabFormRefs.current
      .filter((formRef) => !!formRef.current)
      .map((formRef) => formRef.current?.isValid(forceValidation, forceTouched) ?? true);

    return validFormTabs;
  };

  const isValid = (forceValidation = false, forceTouched = true) => {
    const validTabs = getValidTabs(tabRefs, forceValidation, forceTouched);
    const allTabsValid = validTabs.every((valid) => valid === true);

    const invalid = validTabs.reduce((previousValue, entry, index) => {
      const tabData = data[formSection];

      if (tabData == null) {
        return previousValue;
      }

      return {
        ...previousValue,
        [tabData[index].temporaryId]: !entry,
      };
    }, {});

    setInvalidTabs(invalid);

    return allTabsValid;
  };

  useImperativeHandle(ref, () => ({
    isValid,
  }));

  const createTab = (
    tabData: Partial<EricssonNETargetInfoMonitoringFormData> = {},
    index: number,
  ): TabProp | undefined => {
    if (!formTemplateSection) {
      return;
    }

    if (tabData.temporaryId == null) {
      return;
    }

    const tabRef: { current: Form | null } = {
      current: null,
    };

    tabRefs.current.push(tabRef);

    const refCallback = (node: Form | null) => {
      tabRef.current = node;
    };

    const handleFieldUpdate = (param: FieldUpdateHandlerParam) => onFieldUpdate(param, tabData.temporaryId);

    const panel = (
      <Form
        ref={refCallback}
        name={template.formId ?? template.name}
        defaults={tabData}
        fields={formTemplateSection.fields}
        layout={formTemplateSection.metadata.layout?.rows}
        fieldUpdateHandler={handleFieldUpdate}
        i18nKeyPrefix={i18nKeyPrefix}
      />
    );

    return {
      id: tabData.temporaryId,
      title: `${index + 1}`,
      panel,
      hasErrors: !!invalidTabs?.[tabData.temporaryId] ?? false,
    };
  };

  const tabs = data[formSection]?.map(createTab).filter((entry): entry is TabProp => entry != null) ?? [];

  return (
    <div className="multi-tab-form-section" data-testid={`MultiTabFormSection${id}`}>
      <MultiTab
        tabContainerId={id}
        tabs={tabs}
        addTab={onTabAdd}
        removeTab={onTabRemove}
        minNumberOfTabs={formTemplateSection?.metadata.min}
        maxNumberOfTabs={formTemplateSection?.metadata.max}
        isEditing={isEditing}
        tabClassName="multi-tab-form-tab"
        panelClassName="multi-tab-form-panel"
      />
    </div>
  );
});

export default EricssonNETargetInfoTabs;
